import { DateRangePicker } from '@systemeio/components';
import { LocaleEnum } from '@systemeio/types';
import { ChartBoxSection } from 'modules/main/components/chart-box-section';
import { EventBoxSection } from 'modules/main/components/event-box-section/event-box-section';
import { RevenueLimitNotification } from 'modules/main/components/revenue-limit-notification';
import { useMain } from 'modules/main/hooks/use-main';
import { MainFilterInterface } from 'modules/main/types/main-filter-interface';
import { useEffect, useRef } from 'react';
import { FilterHeader } from 'shared/components/filter-header';
import { toFilterGroup, useFilterDraft } from 'shared/hooks/use-filter-draft';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useMediaQuery from 'shared/hooks/use-media-query';
import useUser from 'shared/hooks/use-user';
import { useFilterWithDate } from 'shared/route-query/hooks/use-filter/use-filter-with-date';
import { getDatePickerMinDate } from 'shared/utils/get-date-picker-min-date';
import { getMonthDateRange, getToday } from 'shared/utils/get-dates';
import { twJoin } from 'tailwind-merge';
const defaultFilter: MainFilterInterface = {
  dateSince: null,
  dateTill: null
};
export const Main = () => {
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale,
    isFreemium
  } = useUser();
  const {
    filter,
    setFilter,
    defaultFilter: defaults
  } = useFilterWithDate(defaultFilter, getMonthDateRange);
  const {
    revenue
  } = useMain(filter);
  const state = useFilterDraft({
    applied: filter,
    defaults,
    onApply: values => setFilter(values)
  });
  const chartBoxRef = useRef<HTMLDivElement>(null);
  const eventBoxRef = useRef<HTMLDivElement>(null);
  const {
    isDesktop
  } = useMediaQuery();
  const chartsHeight = chartBoxRef.current?.getBoundingClientRect().height;
  useEffect(() => {
    if (chartBoxRef.current && eventBoxRef.current && revenue) {
      if (Object.keys(revenue).length > 3 && isDesktop) {
        eventBoxRef.current.style.height = `${chartsHeight}px`;
      } else if (!isDesktop) {
        eventBoxRef.current.style.height = '';
      }
    }
  }, [chartsHeight, isDesktop, revenue]);
  return <div className={'mb-[53px] flex flex-col gap-5'}>
      {isFreemium && <RevenueLimitNotification />}
      <FilterHeader className="desktop:[--filter-field-width:326px]" title={t('dashboard.main.title')} filters={toFilterGroup(state, [{
      key: 'date',
      fields: ['dateSince', 'dateTill'],
      node: <DateRangePicker maxDate={getToday(user?.timezone)} getDefaultDate={() => getMonthDateRange(user?.timezone)} date={[state.draft.dateSince, state.draft.dateTill]} onChange={date => {
        const [dateSince, dateTill] = date;
        state.setDraft({
          dateSince,
          dateTill
        });
      }} timezone={user?.timezone} minDate={getDatePickerMinDate(user?.createdAt, user?.timezone)} intlLocale={intlLocale} wrapperButtonInputClassName={twJoin((user?.dashboardLocale === LocaleEnum.PORTUGUESE || user?.dashboardLocale === LocaleEnum.SPANISH) && 'tracking-[-0.06em]')} />
    }])} />
      <div className={'desktop:flex-row relative flex flex-col justify-between gap-6'}>
        <ChartBoxSection ref={chartBoxRef} className={revenue && Object.keys(revenue).length > 3 ? 'desktop:w-[calc(100%-350px)] w-full' : 'w-full'} filter={filter} />
        <EventBoxSection ref={eventBoxRef} filter={filter} />
      </div>
    </div>;
};
export default Main;